import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Column, Columns, Container } from '@resurs/midas-react'
import { midas } from '@resurs/midas'
import { useLocaleState } from '@merchant-portal/merchant-portal-base'
import {
  getCheckoutSettings,
  getInvoiceData,
  getLogotype,
  getRepresentativeDetails,
  updateCheckoutSettings,
  updateLogotype,
  updateStoreSettings,
} from '../../../store/actions/store-info-actions'
import PaymentInformation from '../PaymentInformation'
import CheckoutSettings from '../CheckoutSettings'
import StoreInfoSettings from '../StoreInfoSettings'
import LogotypeSettings from '../LogotypeSettings'
import { ChainTypes } from '../../../models/chainTypes'
import { Languages } from '../../../models/languages'
import { getText } from '../../../internal/TextLabels'
import Page from '../../../internal/Page'
import { useOnMount } from '../../../util/utility-hooks'

const StoreSettings = ({
  loadingDetails,
  invoiceDataLoading,
  invoiceData,
  currentStore,
  currentRepresentative,
  logotypeLoading,
  logotype,
  checkoutSettingsLoading,
  checkoutStyling,
  termsLinks,
  ...props
}) => {
  const [language] = useState(
    (({ language: tmpLanguage }) =>
      tmpLanguage === Languages.NORWEGIAN ? 'NO' : tmpLanguage.toUpperCase())(
      useLocaleState(),
    ),
  )

  useOnMount(() => {
    props.getRepresentativeDetails()
    props.getInvoiceData()
    props.getLogotype()
    props.getCheckoutSettings()
  })
  const isFranchiseOrNoRelations = () => {
    const chainType = currentRepresentative.chainType || ChainTypes.SINGLE_OWNER
    return (
      chainType === ChainTypes.FRANCHISEE ||
      chainType === ChainTypes.NO_RELATIONS
    )
  }

  const getFieldFromRepOrStore = field =>
    isFranchiseOrNoRelations()
      ? currentStore[field]
      : currentRepresentative[field]

  const updateSettings = (colour, font, termsLink) => {
    const updateCheckoutSettingsData = {
      checkoutStyling: { colour, font },
      termsLink,
      language,
    }
    props.updateCheckoutSettings(updateCheckoutSettingsData)
  }

  const loadingInvoiceData = loadingDetails || invoiceDataLoading
  const loadingLogotype = loadingDetails || logotypeLoading

  return (
    <Container grid960>
      <Columns>
        <Column col1 colMd0 />
        <Column col10 colMd12>
          <Container>
            <div style={{ paddingBottom: '1rem' }}>
              <h3>{getText('storeSettings.storeinfo.theCompany')}</h3>
              <div>{getText('storeSettings.storeinfo.introText')}</div>
            </div>
          </Container>
          <Container>
            <div style={{ paddingBottom: '1rem' }}>
              <h4>{getText('storeSettings.storeinfo.header')}</h4>
              <div>{getText('storeSettings.storeinfo.description')}</div>
            </div>
          </Container>
          {!loadingInvoiceData ? (
            <StoreInfoSettings
              invoiceData={invoiceData}
              country={getFieldFromRepOrStore('country')}
              tradeName={getFieldFromRepOrStore('tradeName')}
              updateStoreSettings={(newInvoiceData, newTradeName) =>
                props.updateStoreSettings(newInvoiceData, newTradeName)
              }
            />
          ) : (
            <div className={midas.loading} />
          )}
          <PaymentInformation />
          <Container>
            <div style={{ paddingBottom: '1rem' }}>
              <h4>{getText('logotypeSettings.header')}</h4>
              <div>{getText('logotypeSettings.description')}</div>
            </div>
          </Container>
          {!loadingLogotype ? (
            <LogotypeSettings
              logotype={logotype}
              updateLogotype={(file, filename) =>
                props.updateLogotype(file, filename)
              }
            />
          ) : (
            <div className={midas.loading} />
          )}
          <div style={{ marginBottom: '200px' }}>
            <Container>
              <div style={{ paddingBottom: '1rem' }}>
                <h4>{getText('storeSettings.checkout.header')}</h4>
                <div>{getText('storeSettings.checkout.description1')}</div>
                <div>{getText('storeSettings.checkout.description2')}</div>
              </div>
            </Container>
            <Page>
              {!checkoutSettingsLoading ? (
                <CheckoutSettings
                  checkoutStyling={checkoutStyling}
                  termsLink={termsLinks[language]}
                  updateCheckoutSettings={updateSettings}
                />
              ) : (
                <div className={midas.loading} />
              )}
            </Page>
          </div>
        </Column>
      </Columns>
    </Container>
  )
}

StoreSettings.propTypes = {
  getRepresentativeDetails: PropTypes.func.isRequired,
  loadingDetails: PropTypes.bool.isRequired,
  currentRepresentative: PropTypes.object,
  currentStore: PropTypes.object,
  updateStoreSettings: PropTypes.func.isRequired,
  // INVOICE DATA
  invoiceDataLoading: PropTypes.bool.isRequired,
  invoiceData: PropTypes.object.isRequired,
  getInvoiceData: PropTypes.func.isRequired,
  // LOGOTYPE
  logotypeLoading: PropTypes.bool.isRequired,
  logotype: PropTypes.object.isRequired,
  getLogotype: PropTypes.func.isRequired,
  updateLogotype: PropTypes.func.isRequired,
  // CHECKOUT SETTINGS
  checkoutSettingsLoading: PropTypes.bool.isRequired,
  checkoutStyling: PropTypes.object.isRequired,
  termsLinks: PropTypes.object.isRequired,
  getCheckoutSettings: PropTypes.func.isRequired,
  updateCheckoutSettings: PropTypes.func.isRequired,
}

StoreSettings.defaultProps = {
  currentRepresentative: null,
  currentStore: null,
}

const mapStateToProps = state => ({
  loadingDetails: state.storeInfo.loadingDetails,
  currentRepresentative: state.storeInfo.currentRepresentative,
  currentStore: state.storeInfo.currentStore,
  invoiceDataLoading: state.storeInfo.invoiceDataLoading,
  invoiceData: state.storeInfo.invoiceData,
  logotypeLoading: state.storeInfo.logotypeLoading,
  logotype: state.storeInfo.logotype,
  checkoutSettingsLoading: state.settings.checkoutSettingsLoading,
  checkoutStyling: state.settings.checkoutSettings.checkoutStyling,
  termsLinks: state.settings.checkoutSettings.termsLinks,
})

const mapDispatchToProps = dispatch => ({
  getRepresentativeDetails: () => {
    dispatch(getRepresentativeDetails())
  },
  getInvoiceData: () => {
    dispatch(getInvoiceData())
  },
  getLogotype: () => {
    dispatch(getLogotype())
  },
  updateLogotype: (file, filename) => {
    dispatch(updateLogotype(file, filename)).then(() => dispatch(getLogotype()))
  },
  getCheckoutSettings: () => {
    dispatch(getCheckoutSettings())
  },
  updateCheckoutSettings: newValues => {
    dispatch(updateCheckoutSettings(newValues))
  },
  updateStoreSettings: (invoiceData, tradeName) => {
    dispatch(updateStoreSettings(invoiceData, tradeName))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(StoreSettings)
